import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentJointWelcomeData,
  type EnrollmentJointWelcomeSubmitInput,
  type EnrollmentJointWelcomeSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Joint Welcome data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointWelcomeData>
 * @example const response = await getEnrollmentJointWelcomeData()
 */
export const getEnrollmentJointWelcomeData = async (context?: DefaultContext): Promise<EnrollmentJointWelcomeData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentJointWelcomeData',
      query: `mutation getEnrollmentJointWelcomeData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            path
            name {
              firstName
              lastName
            }
          }
          jointPerson {
            name {
              firstName
              lastName
            }
          }
        }
      }`,
    },
    {
      input: {},
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointWelcomeData }
  return response.submitJointEnrollment
}

export const useGetEnrollmentJointWelcomeData = createQueryHook<undefined, EnrollmentJointWelcomeData>(
  getEnrollmentJointWelcomeData,
)

/**
 * Submit Enrollment Joint Welcome data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointWelcomeSubmitOutput>
 * @example const response = await submitJointEnrollmentJointWelcomeData();
 */
export const submitEnrollmentJointWelcomeData = async (
  enrollmentWelcomeSubmitInput: EnrollmentJointWelcomeSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointWelcomeSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitJointEnrollmentJointWelcomeData',
      query: `mutation submitJointEnrollmentJointWelcomeData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            path
          }
        }
      }`,
    },
    {
      input: enrollmentWelcomeSubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointWelcomeSubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointWelcomeData = createMutationHook<
  EnrollmentJointWelcomeSubmitInput,
  EnrollmentJointWelcomeSubmitOutput
>(submitEnrollmentJointWelcomeData)
